import React from 'react';
import _ from 'underscore';
import {v4 as uuidv4} from 'uuid';

import {assert, assertAndContinue} from 'Asserts';
import {areDataModsEnabledAnywhere} from 'audit/DataModifications';
import BenchlingConfig from 'BenchlingConfig';
import {StorableSummary} from 'constants/SampleConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import {downloadCSVActionFunction} from 'notebook/structured-tables/action-functions/downloadCSVActionFunction';
import {refreshTableActionFunction} from 'notebook/structured-tables/action-functions/refreshTableActionFunction';
import isReadOnly from 'notebook/structured-tables/columns/isReadOnly';
import {renderFooterDataModSummary} from 'notebook/structured-tables/FooterDataModSummary';
import extractUsableSourceColumns from 'notebook/structured-tables/lookup-columns/extractUsableSourceColumns';
import {lookupColumnCustomEval} from 'notebook/structured-tables/lookup-columns/LookupColumnResolver';
import type {NotebookColumnInfo} from 'notebook/structured-tables/lookup-columns/runLookupConfigModalForNotebook';
import isTagValueStructuredTableColumn from 'notebook/structured-tables/snapshot-fields/isTagValueStructuredTableColumn';
import {snapshotFieldPreviewCustomEval} from 'notebook/structured-tables/snapshot-fields/SnapshotFieldPreviewResolver';
import {DESTINATION_TABLE_TYPES} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingConstants';
import {
  ActionFunctions,
  BaseTagValueStructuredTableColumn,
  CreateStructuredTableProps,
  EvalRowView,
  DedicatedLookupStructuredTableColumn,
  EvalTableView,
  FooterInfoRenderer,
  GenericStructuredTableColumn,
  PrimarySample,
  GetCustomEvalArgsOptions,
  SchemaAndColumnGroup,
  StructuredTable,
  StructuredTableAdapter,
  StructuredTableAdapterOptions,
  StructuredTableAddOperation,
  StructuredTableColumn,
  StructuredTableColumnSummary,
  StructuredTableDomainGraphType,
  StructuredTableKind,
  StructuredTableMetadata,
  StructuredTableRow,
  StructuredTableRowUpdater,
  StructuredTableRowUpdaterModel,
  StructuredTableSchema,
  TableValidationFn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {fieldsPerForRowStructuredTableFormLayout} from 'notebook/structured-tables/StructuredTableForm';
import kindToIcon from 'notebook/structured-tables/util/kindToIcon';
import prepareCellForTableEditable from 'notebook/structured-tables/util/prepareCellForTableEditable';
import {resolveRowDelegateColumn} from 'notebook/structured-tables/util/resolveRowDelegateColumn';
import rowHasUnsubmittedChanges, {
  getCellHasUnsubmittedChangesForRow,
} from 'notebook/structured-tables/util/rowHasUnsubmittedChanges';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {CellIndex, TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import * as ExcessUtil from 'notebook/tables/excess/Util';
import {
  Cell,
  CELL_UI_PROPERTIES,
  ColumnConstraint,
  CustomEvalFnAndType,
  CustomEvalType,
  RowData,
  StructuredTableToTableMetadata,
  RowMetadata,
  Table,
  TableColumnType,
  TableData,
  TableMetadata,
  USER_CREATED_COLUMN_TYPES,
  TableReorderLocks,
} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {isTypedValueEmpty} from 'notebook/tables/typed-values/DataTypes';
import {NoteLinkedObjectType} from 'notebook/util/NoteUtilConstants';
import {SampleLookupInfoForCreateRequest} from 'requests/creation/CreateRequestFromSamplesButton';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import APIUtil from 'util/APIUtil';
import omitKey, {omit} from 'util/omitKey';
import {StageRun} from 'workflows/WorkflowConstants';

import {exportAuditLogsActionFunction} from './action-functions/exportAuditLogsActionFunction';
import {lockTableDimensionsActionFunction} from './action-functions/lockTableDimensionsActionFunction';
import {ColumnDefinition} from './columns/ColumnDefinitions';
import isMissingFieldColumn from './columns/isMissingFieldColumn';
import toColumnConstraint from './columns/toColumnConstraint';
import toColumnLabel from './columns/toColumnLabel';
import toColumnWidth from './columns/toColumnWidth';
import toStructuredTableColumn from './columns/toStructuredTableColumn';
import toStructuredTableColumns from './columns/toStructuredTableColumns';
import {
  getColumnAdapter,
  getEnabledColumnAdapters,
  toStructuredTableColumnsForObjectType,
} from './StructuredTableColumnAdapters';
import {
  getMaxRowsConfig,
  getTableLengthCapacity,
  getTableLengthErrorMessage,
  getTableLengthString,
} from './util/checkTableCapacity';
import getDefaultBlankRow from './util/getBlankRow';
import getSchemasWithColumnGroups from './util/getSchemasWithColumnGroups';
import {isTableLengthLocked} from './util/isTableLengthLocked';
import kindToTableType from './util/kindToTableType';
import {STMethodMemoizer} from './util/structuredTableAdapterMethodMemoizer';

const defaultOptions: Required<StructuredTableAdapterOptions> = {
  allowMultipleRowsInConfigs: true,
  allowNoRowsInConfigs: false,
  allowNoRowsInEntries: false,
  locked: false,
};

const TO_TABLE_CACHE = new STMethodMemoizer();

interface PrepareTableValue<TTableMetadata, TRowMetadata> {
  table: Array<Array<Cell>>;
  rowData: Array<RowData>;
  columnConstraints: Array<ColumnConstraint | null>;
  columnLabels: Array<string | null>;
  widths: Array<number>;
  metadata: StructuredTableToTableMetadata<TTableMetadata>;
  isLengthLocked: boolean;
  allowAddRows: boolean;
  allowNoRows: boolean;
  _tableType: string;
}

/**
 * Returns a merge cell-aware wrapper around ExcessUtil.toSpreadsheetIndex() for the given rows.
 * This should be used for all custom eval arg resolution for structured tables in which merge cells
 * are allowed. Merging across columns is not allowed in structured tables, so only the mergeRowSpan
 * is considered.
 *
 * The function builds a lookup map to keep the runtime to O(R*C) (rather than O(R^2*C)).
 *
 * @param rows Any row-like objects whose cells carry an optional mergeRowSpan.
 */
export function getToSpreadsheetIndexFn(
  rows: ReadonlyArray<{cells: ReadonlyArray<{mergeRowSpan?: number}>}>
): (pos: CellIndex) => string {
  const mergeLookupMap = new Map<string, CellIndex>();
  rows.forEach((row, rowIdx) => {
    row.cells.forEach((cell, colIdx) => {
      if (cell.mergeRowSpan != null) {
        for (let i = 1; i < cell.mergeRowSpan; i++) {
          mergeLookupMap.set(`${rowIdx + i}_${colIdx}`, {row: rowIdx, column: colIdx});
        }
      }
    });
  });

  return (pos: CellIndex): string => {
    const mergeCellPos = mergeLookupMap.get(`${pos.row}_${pos.column}`);
    if (mergeCellPos) {
      return ExcessUtil.toSpreadsheetIndex(mergeCellPos);
    }

    return ExcessUtil.toSpreadsheetIndex(pos);
  };
}

/**
 * A generic StructuredTableAdapter that can be used if there's no
 * special handling needed.  It has two type parameters which allow
 * instances to extend what table metadata and row metadata is stored,
 * in addition to the standard table data.
 */
export default abstract class GenericStructuredTableAdapter<
  TTableMetadata,
  TRowMetadata,
> implements StructuredTableAdapter<TTableMetadata, TRowMetadata> {
  kind: StructuredTableKind;
  options: StructuredTableAdapterOptions;

  constructor(kind: StructuredTableKind, options?: StructuredTableAdapterOptions) {
    this.kind = kind;

    this.options = {
      ...defaultOptions,
      ...options,
    };
  }

  getActionFunctions(
    tableMetadata: TTableMetadata,
    schemas: Array<StructuredTableSchema>
  ): ActionFunctions<TTableMetadata, TRowMetadata> {
    return {
      secondary: [refreshTableActionFunction],
      overflow: [exportAuditLogsActionFunction, downloadCSVActionFunction],
      footer: [lockTableDimensionsActionFunction],
    };
  }

  getFooterInformation(): Array<FooterInfoRenderer<TTableMetadata, TRowMetadata>> {
    if (areDataModsEnabledAnywhere() && BenchlingConfig.ELN_ENABLE_TABLE_DATA_MODIFICATION_SUMMARY) {
      return [renderFooterDataModSummary];
    }
    return [];
  }

  getAddFromSourceOperations(
    entryId: string,
    structuredTable?: StructuredTable<TTableMetadata, TRowMetadata>
  ): Array<StructuredTableAddOperation<TTableMetadata, TRowMetadata>> {
    return [];
  }

  abstract getDisplayName(structuredTable: StructuredTable<TTableMetadata, TRowMetadata>): string;

  shouldShowAsForm(table: StructuredTable<TTableMetadata, TRowMetadata>): boolean {
    return false;
  }

  renderFormContents(columns: Array<ColumnDefinition>, formFields: Array<JSX.Element>): JSX.Element | null {
    return <Row bufferRowReset={true}>{fieldsPerForRowStructuredTableFormLayout(columns, formFields)}</Row>;
  }

  // Override this method only if you need control over the layout of your form.
  renderFormField(editor: JSX.Element, columnDefinition: ColumnDefinition, columnIndex: number): JSX.Element {
    return <Col sm={4}>{editor}</Col>;
  }

  shouldSuppressTitleBar(table: StructuredTable<TTableMetadata, TRowMetadata>): boolean {
    return false;
  }

  shouldShowFormulaBox(table: StructuredTable<TTableMetadata, TRowMetadata>): boolean {
    return !this.shouldSuppressTitleBar(table);
  }

  /**
   * This method returns the row metadata to use when converting from
   * a Table into a StructuredTable.  The rowData parameter can be
   * null if we are creating new rows.
   *
   * @param tableMetadata the given table's metadata
   * @param rowData the RowData for the row or null if there is no RowData yet
   */
  getRowMetadata(tableMetadata: TTableMetadata, rowData: RowData | null): TRowMetadata {
    const rowMetadata = rowData ? this.getRowMetadataFromRowData(rowData) : null;
    if (rowMetadata == null) {
      return this.getBlankRowMetadata(tableMetadata);
    }
    return rowMetadata;
  }

  /**
   * Returns the StructuredTable RowMetadata, if it exists, from the RowData.
   *
   * @param rowData the Table's RowData object
   */
  getRowMetadataFromRowData(rowData: RowData): TRowMetadata | null {
    if (rowData.rowMetadata) {
      // RowData does not have a type parameter so we need to cast it to our specific type here
      return rowData.rowMetadata as TRowMetadata;
    }
    return null;
  }

  /**
   * Returns the row metadata for a new, blank row.
   *
   * @param tableMetadata the metadata for the StructuredTable
   */
  abstract getBlankRowMetadata(tableMetadata: TTableMetadata): TRowMetadata;

  createTable(
    createProps: CreateStructuredTableProps<TTableMetadata>
  ): Omit<StructuredTable<TTableMetadata, TRowMetadata>, 'cid'> {
    if (this.options.locked) {
      assert(false, 'Not implemented');
    }
    const {entryId, name, schemas, metadata, selectorsById, isConfig, structuredTableInsertionContext} =
      createProps;
    const columns = this.getInitialColumns({
      entryId,
      metadata,
      schemas,
      selectorsById,
      structuredTableInsertionContext,
    }).map((column) => {
      const defaultValue = createProps.defaultValuesByColumnType?.[column.columnType];
      return defaultValue ? {...column, defaultValue} : column;
    });
    const rows = [this.getBlankRow(metadata, 0, columns)];
    return {
      columns,
      rows,
      name,
      kind: this.kind,
      isConfig: isConfig || (entryId ? APIUtil.isEntryOrSubOrWorkflowTemplate(entryId) : false),
      schemas,
      provenanceUuid: uuidv4(),
      fileId: entryId ?? undefined,
      apiId: undefined,
      numApiRowsToAdd: 0,
      metadata: {
        schemaMetadata: schemas.map((schema) => ({
          schemaId: schema.id,
          group: schema.columnGroup || null,
          parentSchemaId: schema.parentSchemaId || null,
        })),
        ...metadata,
      },
      type: NoteLinkedObjectType.STRUCTURED_TABLE,
      delegatedColumns: [],
      filterInfo: undefined,
    };
  }

  getInitialColumns({
    entryId,
    metadata,
    schemas,
    selectorsById,
    structuredTableInsertionContext,
  }: {
    entryId: string | null;
    metadata: TTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[selectorId: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    return this.getAllSupportedColumns({
      entryId,
      metadata,
      schemas,
      selectorsById,
      structuredTableInsertionContext,
    });
  }

  /**
   * Get all the columns that the table can include.
   * Columns can be created when a table is inserted, or they can be added manually afterward.
   *
   * This is the public interface, and subclasses shouid not override it!
   * Instead, to add your own columns, please implement your own _getAllSupportedColumns() method.
   * Make sure to invoke super._getAllSupportedColumns().
   */
  getAllSupportedColumns(params: {
    entryId: string | null;
    metadata: TTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[selectorId: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    return this._getAllSupportedColumns(params).map((column) =>
      this.injectFrontEndMetadata({column, schemas: params.schemas, tableMetadata: params.metadata})
    );
  }

  /**
   * Subclasses should override this method to do the work.
   * Incorporate the result of `super._getAllSupportedColumns()` in whatever way makes sense for your table.
   * @private
   */
  _getAllSupportedColumns({
    entryId,
    metadata,
    schemas,
    selectorsById,
    structuredTableInsertionContext,
  }: {
    entryId: string | null;
    metadata: TTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[selectorId: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    if (this.options.locked) {
      assert(false, 'Not implemented');
    }
    const schemaFieldColumns = toStructuredTableColumns({
      schemas,
      selectorsById,
      metadata,
    });
    const additionalColumns = this.getDomainGraphTypes(schemas, metadata).flatMap(
      ({objectSpec, columnGroup}) =>
        toStructuredTableColumnsForObjectType(objectSpec, columnGroup, structuredTableInsertionContext)
    );
    return [...schemaFieldColumns, ...additionalColumns];
  }

  injectFrontEndMetadata({
    column,
    schemas,
    tableMetadata,
  }: {
    column: StructuredTableColumn;
    schemas: Array<SchemaAndColumnGroup>;
    tableMetadata: TTableMetadata;
  }): StructuredTableColumn {
    return column;
  }

  getBlankRow(
    tableMetadata: TTableMetadata,
    rowIndex: number,
    columns: Array<StructuredTableColumn>,
    initialRowMetadata?: TRowMetadata
  ): StructuredTableRow<TRowMetadata> {
    if (this.options.locked) {
      assert(false, 'Not implemented');
    }

    return {
      isFinalized: false,
      rowId: uuidv4(),
      cells: getDefaultBlankRow(rowIndex, columns, null),
      metadata: initialRowMetadata || this.getBlankRowMetadata(tableMetadata),
      lastSyncedUpstreamValues: null,
    };
  }

  toTableRow(
    cells: Array<Cell>,
    metadata: TTableMetadata,
    rowData: RowData
  ): StructuredTableRow<TRowMetadata> {
    if (this.options.locked) {
      assert(false, 'Not implemented');
    }
    assert(rowData.isFinalized != null, 'isFinalized was null when attempting to create a row');
    assert(rowData.rowId != null, 'unable to determine the rowId from a TableEditable row');
    assert(cells.length > 0, 'row was empty');
    const row = {
      isFinalized: rowData.isFinalized,
      rowId: rowData.rowId,
      cells: cells.map((cell) => omit(cell, ...CELL_UI_PROPERTIES)),
      metadata: this.getRowMetadata(metadata, omitKey(rowData, 'uiData')),
      lastSyncedUpstreamValues: rowData.lastSyncedUpstreamValues,
    };
    if (row.lastSyncedUpstreamValues === undefined) {
      delete row.lastSyncedUpstreamValues;
    }
    return row;
  }

  getCustomEvalArgs(
    tableView: EvalTableView<TTableMetadata, TRowMetadata>,
    cellIndex: CellIndex,
    options: {
      toSpreadsheetIndex: (cellId: CellIndex) => string;
    }
  ): Array<string> {
    const {toSpreadsheetIndex} = options;
    const {columns} = tableView;
    const {column} = this.getRowAndColumnForCellIndex(tableView, cellIndex);
    if (column.columnType === TableColumnType.DEDICATED_LOOKUP) {
      for (let i = 0; i < columns.length; i++) {
        if (
          columns[i].provenanceUuid ===
          (column as DedicatedLookupStructuredTableColumn).inputColumnProvenanceUuid
        ) {
          return [`=${toSpreadsheetIndex({...cellIndex, column: i})}`];
        }
      }
    }

    if (isTagValueStructuredTableColumn(column) && column.schemaFieldIsSnapshot) {
      let snapshotFieldInputIndex = '';
      let snapshotFieldGridPositionInputIndex = '';
      for (let i = 0; i < columns.length; i++) {
        const otherColumn = columns[i];
        if (
          isTagValueStructuredTableColumn(otherColumn) &&
          column.snapshotFieldInputSchemaFieldApiId === otherColumn.schemaFieldApiId
        ) {
          snapshotFieldInputIndex = `=${toSpreadsheetIndex({...cellIndex, column: i})}`;
        }
        if (
          isTagValueStructuredTableColumn(otherColumn) &&
          column.snapshotFieldGridPositionInputSchemaFieldApiId === otherColumn.schemaFieldApiId
        ) {
          snapshotFieldGridPositionInputIndex = `=${toSpreadsheetIndex({
            ...cellIndex,
            column: i,
          })}`;
        }
        if (
          otherColumn.columnType === TableColumnType.PROCESS_TASK &&
          column.snapshotFieldInputIsProcessTask
        ) {
          snapshotFieldInputIndex = `=${toSpreadsheetIndex({...cellIndex, column: i})}`;
        }
      }
      if (snapshotFieldInputIndex || snapshotFieldGridPositionInputIndex) {
        return [snapshotFieldInputIndex, snapshotFieldGridPositionInputIndex];
      }
    }

    const columnAdapter = getColumnAdapter(column.columnType);
    if (columnAdapter) {
      const schemasWithColumnGroups =
        getSchemasWithColumnGroups(tableView.metadata.schemaMetadata, tableView.schemas) ?? [];
      const coreObjectTypes = this.getDomainGraphTypes(schemasWithColumnGroups, tableView.metadata);
      return columnAdapter.getCustomEvalArgs(tableView, cellIndex, {
        toSpreadsheetIndex,
        coreObjectTypes,
      });
    }

    return this.getTableSpecificCustomEvalArgs(tableView, cellIndex, options);
  }

  getTableSpecificCustomEvalArgs(
    tableView: EvalTableView<TTableMetadata, TRowMetadata>,
    cellIndex: CellIndex,
    options: GetCustomEvalArgsOptions
  ): Array<string> {
    return [];
  }

  getCustomEvalFn(
    tableView: EvalTableView<TTableMetadata, TRowMetadata>,
    cellIndex: CellIndex
  ): CustomEvalFnAndType<{
    tableMetadata: TTableMetadata;
    rowMetadata: TRowMetadata;
    fileId?: string | null;
  }> | null {
    const {column, row} = this.getRowAndColumnForCellIndex(tableView, cellIndex);
    if (column.columnType === TableColumnType.DEDICATED_LOOKUP) {
      return {
        customEvalFn: lookupColumnCustomEval,
        customEvalType: CustomEvalType.DEFAULT,
      };
    }
    if (isTagValueStructuredTableColumn(column) && column.schemaFieldIsSnapshot && !row.isFinalized) {
      return {
        customEvalFn: snapshotFieldPreviewCustomEval,
        customEvalType: CustomEvalType.DEFAULT,
      };
    }
    const columnAdapter = getColumnAdapter(column.columnType);
    if (columnAdapter) {
      return columnAdapter.getCustomEvalFn(tableView, cellIndex);
    }
    return this.getTableSpecificCustomEvalFn(tableView, cellIndex);
  }

  getTableSpecificCustomEvalFn(
    tableView: EvalTableView<TTableMetadata, TRowMetadata>,
    cellIndex: CellIndex
  ): CustomEvalFnAndType<{tableMetadata: TTableMetadata; rowMetadata: TRowMetadata}> | null {
    return null;
  }

  /** The following column types are NOT locked when the row is finalized. Rarely do you need to override this. */
  getNonFinalizingColumnTypes(): Array<TableColumnType> {
    return [
      TableColumnType.TAG_VALUE,
      TableColumnType.CUSTOM,
      TableColumnType.PROCEDURE_CONDITION_REPLICATES,
    ];
  }

  /**
   * Lock any cell which is in a read only column or if the row is finalized and it's not a tag value/ custom
   * or if the cell is already marked as locked
   *
   * If this method is overridden in a subclass to allow a finalized cell to be editable, you must update
   * benchling/notebook/tables/validation/cells.py:validate_cell_valid_for_column to skip validation for the
   * corresponding column type; otherwise, the entry may fail to save if the user drafts an invalid value.
   */
  isPositionReadOnly(
    currentColumnIndex: number,
    row: EvalRowView<TRowMetadata>,
    structuredTable: EvalTableView<TTableMetadata, TRowMetadata>
  ): boolean {
    // get row columns in case of any row delegate columns
    const rowColumns = structuredTable.columns.map((column) =>
      resolveRowDelegateColumn(column, row, structuredTable.delegatedColumns)
    );
    const currentColumn = rowColumns[currentColumnIndex];
    const columnType = currentColumn.columnType;
    return (
      isReadOnly(currentColumn) ||
      isMissingFieldColumn(currentColumn, structuredTable.schemas) ||
      Boolean(row.cells[currentColumnIndex].locked) ||
      (row.isFinalized &&
        !this.getNonFinalizingColumnTypes().includes(currentColumn.columnType) &&
        (getColumnAdapter(columnType)?.doesFinalize ?? true))
    );
  }

  private _prepareTable(
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>
  ): PrepareTableValue<TTableMetadata, TRowMetadata> {
    const {
      cid,
      columns,
      delegatedColumns,
      kind,
      metadata,
      name,
      provenanceUuid,
      fileId,
      apiId,
      numApiRowsToAdd,
      rows,
      schemas,
      isConfig,
    } = structuredTable;
    const toTableMetadata: StructuredTableToTableMetadata<TTableMetadata> = {
      cid,
      kind,
      provenanceUuid,
      fileId,
      apiId,
      numApiRowsToAdd,
      schemas,
      metadata,
      isConfig,
    };

    // StageRuns aren't supported for StructuredTables yet
    const stageRuns: Array<StageRun> = [];

    const columnConstraints = columns.map((structuredTableColumn) => ({
      ...toColumnConstraint({structuredTableColumn, schemas, stageRuns, kind, ...metadata}),
      isColumnUserConfigurable: this.isColumnUserConfigurable(structuredTable, structuredTableColumn),
    }));

    // convert delegated columns into ColumnConstraints and insert them into the row data
    const delegatedConstraintsMap: Map<string, ColumnConstraint> = new Map();
    for (const structuredTableColumn of delegatedColumns) {
      delegatedConstraintsMap.set(structuredTableColumn.provenanceUuid, {
        ...toColumnConstraint({structuredTableColumn, schemas, stageRuns, kind, ...metadata}),
        isColumnUserConfigurable: this.isColumnUserConfigurable(structuredTable, structuredTableColumn),
      });
    }

    function assembleDelegatedConstraintsForRow(metadata: TRowMetadata): Map<string, ColumnConstraint> {
      const constraintMap: Map<string, ColumnConstraint> = new Map();
      if (metadata == null) {
        return constraintMap;
      }
      const delegatedColumnMap = (metadata as RowMetadata).delegatedColumns ?? new Map();
      for (const colIds of Object.entries(delegatedColumnMap)) {
        assert(colIds[1] != null, 'Delegated column ID is null');
        const delegatedConstraint = delegatedConstraintsMap.get(colIds[1]);
        if (delegatedConstraint != null) {
          constraintMap.set(colIds[0], delegatedConstraint);
        }
      }
      return constraintMap;
    }

    const rowData: Array<RowData> = rows.map(
      (tableRow) =>
        ({
          ...omit(tableRow, 'cells', 'metadata'),
          rowMetadata: tableRow.metadata,
          delegatedColumnConstraints: assembleDelegatedConstraintsForRow(tableRow.metadata),
        }) as RowData
    );
    const columnLabels = columns.map((structuredTableColumn) =>
      toColumnLabel({structuredTableColumn, schemas, stageRuns, ...metadata})
    );
    const widths = columns.map((column) => toColumnWidth(column));
    const isLengthLocked = isTableLengthLocked(structuredTable);
    const allowNoRows = Boolean(
      isConfig ? this.options.allowNoRowsInConfigs : this.options.allowNoRowsInEntries
    );
    if (rows.length === 0) {
      // TableEditable requires at least one row.  Somehow this table has lost all of it's rows,
      // which may have been caused by something like ISD-1514.  We patch up the table in this
      // case with a blank row so we don't have a crash later on.
      rows.push(this.getBlankRow(metadata, 0, columns));
      if (!allowNoRows) {
        assertAndContinue(false, 'Table has no rows', {
          extra: {tableName: name, tableProvenanceUuid: provenanceUuid},
        });
      }
    }

    const toSpreadsheetIndex = getToSpreadsheetIndexFn(rows);

    const table = rows.map((row, rowIndex) =>
      row.cells.map((cell, columnIndex) => {
        const cellIndex: CellIndex = {row: rowIndex, column: columnIndex};
        const locked = this.isPositionReadOnly(columnIndex, row, structuredTable);
        const customEvalInfo = this.getCustomEvalFn(structuredTable, cellIndex);
        return {
          ...prepareCellForTableEditable(cell, columns, rowIndex, columnIndex),
          customEvalArgs: this.getCustomEvalArgs(structuredTable, cellIndex, {toSpreadsheetIndex}),
          customEvalOptions: {tableMetadata: metadata, rowMetadata: row.metadata},
          customEval: customEvalInfo?.customEvalFn,
          customEvalType: customEvalInfo?.customEvalType,
          locked,
        };
      })
    );

    const allowAddRows = Boolean(
      (!isConfig || this.options.allowMultipleRowsInConfigs) &&
      table.length < getMaxRowsConfig(true, this.kind) &&
      !isLengthLocked
    );

    return {
      table,
      rowData,
      columnConstraints,
      metadata: toTableMetadata,
      columnLabels,
      widths,
      _tableType: kindToTableType(this.kind, isConfig),
      isLengthLocked,
      allowAddRows,
      allowNoRows,
    };
  }

  /**
   * Returns whether the column can be modified (deleted) in the context of that structured table
   *
   * @param structuredTable - the structured table containing the columns
   * @param column - the StructuredTableColumn that we're evaluating editability of
   * @returns whether the structured table allows for the column to be modified, false if column is null
   */
  isColumnUserConfigurable(
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
    column: StructuredTableColumn | null
  ): boolean {
    const editableColumnTypes = [
      TableColumnType.TAG_VALUE,
      TableColumnType.QUANTITY,
      TableColumnType.CONCENTRATION,
      TableColumnType.ALIAS,
      TableColumnType.BASES,
      TableColumnType.SMILES,
      ...USER_CREATED_COLUMN_TYPES,
    ];

    const type = column?.columnType;
    if (type == null) {
      // If we don't know what kind of column this is, we'll just be safe and say it's not editable.
      // This _shouldn't_ occur for StructuredTables but there might be some weird historical detail that we're missing.
      return false;
    }
    return editableColumnTypes.includes(type) || (getColumnAdapter(type)?.isUserConfigurable ?? false);
  }

  toTable(structuredTable: StructuredTable<TTableMetadata, TRowMetadata>): Table {
    const {apiId, provenanceUuid, fileId, numApiRowsToAdd, filterInfo} = structuredTable;
    // Use a memoized function to avoid recalculating the table every time
    const {
      allowAddRows,
      allowNoRows,
      columnConstraints,
      columnLabels,
      isLengthLocked,
      metadata,
      rowData,
      table,
      widths,
      _tableType,
    } = TO_TABLE_CACHE.getFunctionValue<
      PrepareTableValue<TTableMetadata, TRowMetadata>,
      typeof this._prepareTable
    >({
      functionIdentifier: '_prepareTable',
      cacheKey: provenanceUuid,
      functionToMemoize: this._prepareTable.bind(this),
      args: [structuredTable],
    });

    return {
      columnConstraints,
      columnLabels,
      name: structuredTable.name,
      rowData,
      table,
      _tableType,
      widths,
      metadata,
      defaultAffixedColumnCount: 1,
      frozenColumnCount: structuredTable.metadata.frozenColumnCount,
      allowAddRows,
      allowNoRows,
      allowRowRemoval: !isLengthLocked,
      maxRows: this.getTableLengthCapacity(structuredTable, true),
      allowColumnEdits: true,
      disallowCustomColumns: true,
      headerCol: false,
      headerRow: false,
      lockedDims: isLengthLocked,
      provenanceUuid,
      apiId,
      fileId,
      numApiRowsToAdd,
      filterInfo,
    };
  }

  fromTable(updatedTable: TableData): StructuredTable<TTableMetadata, TRowMetadata> {
    if (this.options.locked) {
      assert(false, 'Not implemented');
    }
    const {metadata: tableMetadata} = updatedTable;
    if (!isStructuredTableMetadata<TTableMetadata>(tableMetadata)) {
      throw new Error('Attempting to transform a table which is not a StructuredTable');
    }

    assert(updatedTable.rowData != null, 'Expected rowData to not be null or undefined');

    // Grab the fields we need for StructuredTable from the wrapped TableData metadata
    const {cid, kind, provenanceUuid, apiId, fileId, schemas, metadata: tableSubtypeMetadata} = tableMetadata;
    const {columnConstraints, rowData, table, numApiRowsToAdd, filterInfo, name} = updatedTable;
    const columnsMaybeNull: Array<StructuredTableColumn | null> = columnConstraints.map(
      (constraint: ColumnConstraint | null) => toStructuredTableColumn(constraint)
    );
    let columns: Array<StructuredTableColumn> = _.compact(columnsMaybeNull);
    assert(
      columns.length === columnsMaybeNull.length,
      'Column constraint could not be turned into a Structured Table Column'
    );
    // Delegated constraints may be duplicated if more than one delegated cell uses the same delegated column
    const rawDelegatedConstraints = rowData.flatMap((row) => [
      ...(row.delegatedColumnConstraints != null ? row.delegatedColumnConstraints.values() : []),
    ]);
    const delegatedConstraints = _.unique(rawDelegatedConstraints);

    const delegatedColumnsMaybeNull: Array<StructuredTableColumn | null> = delegatedConstraints.map(
      (constraint: ColumnConstraint | null) => toStructuredTableColumn(constraint)
    );
    const delegatedColumns: Array<StructuredTableColumn> = _.compact(delegatedColumnsMaybeNull);
    assert(
      delegatedColumns.length === delegatedColumnsMaybeNull.length,
      'Delegated column constraint could not be turned into a Structured Table Column'
    );

    // Some custom handling for tables in templates
    if (tableMetadata.isConfig) {
      columns = TableUtil.getStructuredTableColumnsForTemplates(table, columns);
    }

    columns = columns.map((column, colIndex) => ({
      ...column,
      width: updatedTable.widths[colIndex],
    }));

    const rows: Array<StructuredTableRow<TRowMetadata>> = table.map((row, index) => {
      if (rowData != null && index < rowData.length && Object.keys(rowData[index]).length > 0) {
        return this.toTableRow(row, tableSubtypeMetadata, rowData[index]);
      } else {
        // This case is triggered when we create new rows or when we paste values
        const blankRow = this.getBlankRow(tableSubtypeMetadata, index, columns);
        return {
          ...blankRow,
          cells: row.map((cell, columnIndex) => {
            // The row given to us may contain a value if this extended table
            // was from a paste. But if it doesn't have a text or typed value,
            // then we should use the default cell value in the blank row, which
            // may come from a template.
            if (
              TableUtil.isCellEmpty(cell) &&
              (cell.typedValue == null || isTypedValueEmpty(cell.typedValue))
            ) {
              return blankRow.cells[columnIndex];
            }
            return omit(cell, ...CELL_UI_PROPERTIES);
          }),
        };
      }
    });
    const {frozenColumnCount: _dropFrozen, ...restMetadata} = tableSubtypeMetadata;
    const resolvedMetadata: StructuredTableMetadata & TTableMetadata =
      updatedTable.frozenColumnCount != null
        ? ({...restMetadata, frozenColumnCount: updatedTable.frozenColumnCount} as StructuredTableMetadata &
            TTableMetadata)
        : (restMetadata as StructuredTableMetadata & TTableMetadata);

    return {
      cid,
      columns,
      delegatedColumns,
      kind,
      name,
      isConfig: tableMetadata.isConfig,
      provenanceUuid,
      apiId,
      fileId,
      numApiRowsToAdd,
      schemas,
      metadata: resolvedMetadata,
      rows,
      type: NoteLinkedObjectType.STRUCTURED_TABLE,
      filterInfo,
    };
  }

  /**
   * Get a table's column definitions from its persisted columns
   */
  getColumnDefinitions(
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>
  ): Array<ColumnDefinition> {
    return this.toTable(structuredTable).columnConstraints.map(
      // In structured tables, all columns should have a column definition.
      (columnConstraint) => columnConstraint?.columnDefinition!
    );
  }

  /**
   * Our GenericStructuredTableAdapter does not support getting primary samples from the table (yet)
   *
   * @param structuredTable
   */
  // Keep in sync with resolve_primary_sample_ids_for_row in
  // benchling/notebook/structured_tables/resync/primary_samples.py, which dispatches to the per-kind
  // TableInstantiator.get_primary_sample_ids_for_row. The default (no override) returns no samples; that
  // dispatch is tested by primary_samples_test.py and the default by
  // tests/unit/notebook/structured_tables/instantiators/generic_table_instantiator_test.py.
  getPrimarySamples(
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
    computedTable?: Array<Array<EvaluatedCell>> | null
  ): {[rowId: string]: Array<PrimarySample>} {
    const ret = {} as {
      [rowId: string]: Array<PrimarySample>;
    };
    structuredTable.rows.forEach(({rowId}) => {
      ret[rowId] = [];
    });
    return ret;
  }

  skipUnsubmittedIndicatorsForRow(
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
    row: StructuredTableRow<TRowMetadata>
  ): boolean {
    return false;
  }

  getRowUnsubmittedStatuses(
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
    computedTable: Array<Array<EvaluatedCell>>
  ): Array<boolean> {
    return structuredTable.rows.map((row, i) => {
      if (this.skipUnsubmittedIndicatorsForRow(structuredTable, row)) {
        return false;
      }
      // get specific columns for each row in case there are any delegated columns
      const columns = this.getColumnsForRow(structuredTable, row);
      return rowHasUnsubmittedChanges(computedTable[i], row, columns, structuredTable.schemas);
    });
  }

  getCellUnsubmittedStatuses(
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
    computedTable: Array<Array<EvaluatedCell>>
  ): Array<Array<boolean>> {
    return structuredTable.rows.map((row, i) => {
      if (this.skipUnsubmittedIndicatorsForRow(structuredTable, row)) {
        return computedTable[i].map(() => false);
      }
      // get specific columns for each row in case there are any delegated columns
      const columns = this.getColumnsForRow(structuredTable, row);
      return getCellHasUnsubmittedChangesForRow(computedTable[i], row, columns, structuredTable.schemas);
    });
  }

  /**
   * The GenericStructuredTableAdapter does not support getting sample ids for requests.
   * @param structuredTable
   */
  getSampleInfosForRequest(
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
    computedTable?: Array<Array<EvaluatedCell>> | null
  ): Array<SampleLookupInfoForCreateRequest> {
    return [];
  }

  /**
   * The GenericStructuredTableAdapter doesn't perform any additional table validation checks.
   */
  getTableValidationFns(): Array<TableValidationFn<TTableMetadata, TRowMetadata>> {
    return [];
  }

  /**
   * The default appearance for a structured table in preview mode is to disable error warning and lock all cells
   */
  prepareForPreview(
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>
  ): StructuredTable<TTableMetadata, TRowMetadata> {
    const ret = {...structuredTable, isConfig: true}; // This disables the red error dog ears
    ret.columns = structuredTable.columns.map((col) => ({...col, isReadOnly: true})); // This locks all cells in the table
    return ret;
  }

  /**
   * The GenericStructuredTableAdapter does not support identifying the primary column for process creation.
   * @param structuredTable
   */
  getColumnSummariesForCreateProcessShortcut(
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>
  ): Array<StructuredTableColumnSummary & {columnIndex: number}> {
    return [];
  }

  getStoreDataForLiveValues(structuredTable: StructuredTable<TTableMetadata, TRowMetadata>): {
    fileSummariesById?: {[bioentityId: string]: BioentitySummary};
    storableSummariesById?: {[storableId: string]: StorableSummary};
  } {
    return {};
  }

  areRequiredSchemasArchived(structuredTable: StructuredTable<TTableMetadata, TRowMetadata>): boolean {
    return false;
  }

  isReadOnly(): boolean {
    return false;
  }

  allowPasteTable(): boolean {
    return true;
  }

  // Keep in sync with TableInstantiator.allow_delete_table in
  // benchling/notebook/structured_tables/instantiators/generic_table_instantiator.py
  allowDeleteTable(structuredTable: StructuredTable<TTableMetadata, TRowMetadata>): {
    canDelete: boolean;
    reason?: string;
  } {
    if (
      BenchlingConfig.ENABLE_SUBMITTED_TABLE_DELETION_PREVENTION &&
      structuredTable.rows.some((row) => row.isFinalized)
    ) {
      return {canDelete: false, reason: 'Submitted structured tables cannot be deleted'};
    }
    return {canDelete: true};
  }

  allowReorderDimensions(structuredTable: StructuredTable<TTableMetadata, TRowMetadata>): TableReorderLocks {
    return {
      [TableDimension.ROW]: true,
      [TableDimension.COLUMN]: true,
    };
  }

  /**
   * A StructuredTableRowUpdater which should be used for this table implementation.  These are
   * used when refreshing the table or checking for stale values.
   *
   * This row updater should focus on kind-specific columns and any columns that are not handled by
   * ./StructuredTableColumnAdapters.ts.
   *
   * Callers should generally use `getAllRowUpdaters()` instead of calling this method directly.
   */
  getTableKindRowUpdater(): StructuredTableRowUpdater<StructuredTableRowUpdaterModel, TRowMetadata> | null {
    return null;
  }

  getAllRowUpdaters<TIds>(
    columns?: Array<GenericStructuredTableColumn<TIds>>
  ): Array<StructuredTableRowUpdater> {
    const updaters: Array<StructuredTableRowUpdater> = [];
    const kindAwareRowUpdater = this.getTableKindRowUpdater();
    if (kindAwareRowUpdater) {
      updaters.push(kindAwareRowUpdater);
    }
    // Column-based row updaters will no-op if the "sample" they receive is unsupported.
    for (const adapter of getEnabledColumnAdapters(columns)) {
      updaters.push(adapter.rowUpdater);
    }
    return updaters;
  }

  getTableLengthErrorMessage(
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
    newTableLength: number,
    isTableEditable?: boolean,
    numPendingInAdded?: number
  ): string | null {
    const lockRowCount = isTableLengthLocked(structuredTable);

    return getTableLengthErrorMessage({
      structuredTableKind: structuredTable.kind,
      currentTableLength: structuredTable.rows.length,
      newTableLength,
      isTableEditable,
      allowAddRows: !lockRowCount,
      allowRemoveRows: !lockRowCount,
      numApiRowsPending: structuredTable.numApiRowsToAdd,
      numPendingInAdded,
    });
  }

  getTableLengthCapacity(
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
    isTableEditable?: boolean
  ): number {
    return getTableLengthCapacity({
      structuredTableKind: structuredTable.kind,
      isTableEditable,
      numApiRowsPending: structuredTable.numApiRowsToAdd,
    });
  }

  getTableLengthString(
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
    isTableEditable?: boolean
  ): string {
    return getTableLengthString({
      structuredTableKind: structuredTable.kind,
      currentTableLength: structuredTable.rows.length,
      isTableEditable,
      numApiRowsPending: structuredTable.numApiRowsToAdd,
    });
  }

  /**
   * Returns true if cells within the specified column should be allowed to be merged. Subclasses should not
   * override this method as it contains logic common to all structured tables. Instead, override
   * allowMergedCellRangeInColumn, which is provided specifically for subclasses to add table-specific rules.
   *
   * @param structuredTable The table containing the cells to be merged.
   * @param column The column containing the cells to be merged.
   * @returns true iff the merge should be allowed.
   */
  allowMergedCellRange(
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
    column: StructuredTableColumn
  ): boolean {
    if (this.isReadOnly()) {
      return false;
    }

    if (column.columnType === TableColumnType.DEDICATED_LOOKUP) {
      return false;
    }
    if (column.isReadOnly || column.isProtected || column.isArchived) {
      return false;
    }

    if (column.columnType === TableColumnType.TAG_VALUE) {
      if (
        (column as BaseTagValueStructuredTableColumn<unknown>).schemaFieldIsComputed ||
        (column as BaseTagValueStructuredTableColumn<unknown>).schemaFieldIsSnapshot
      ) {
        return false;
      }
    }

    return this.allowMergedCellRangeInColumn(structuredTable, column);
  }

  /**
   * Subclasses should override this method as needed to further restrict merged cell ranges.
   */
  allowMergedCellRangeInColumn(
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
    column: StructuredTableColumn
  ): boolean {
    return true;
  }

  /**
   * For each schema, indicate what {@link ObjectType}s are supported.
   *
   * This can be used to determine what platform-capability-linked columns should be available to a table.
   *
   * Override the implemenation to opt-in for column-based capabilities via StructuredTableColumnAdapters.
   */
  getDomainGraphTypes(
    schemas: Array<SchemaAndColumnGroup>,
    metadata: TTableMetadata
  ): Array<StructuredTableDomainGraphType> {
    // TODO(BNCH-66819): Use instrospection API to look up object types given a hydrated schema.
    // See `RegistrationStructuredTableAdapter.getDomainGraphTypes`
    return [];
  }

  allowSingleInputs(structuredTable: StructuredTable<TTableMetadata, TRowMetadata>): boolean {
    return true;
  }

  /**
   * Return the icon that should identify a particular table. By default, uses the table kind's defined icon.
   * This only needs to be overridden if the INSTANCE of a particular structured table affects the icon.
   * (ex: something in the metadata changes the icon)
   */
  getTableIcon(structuredTable: StructuredTable<TTableMetadata, TRowMetadata>): BenchlingIconName {
    return kindToIcon(structuredTable.kind);
  }

  /**
   * For a given row, get the columns that particular row has in order. Notably, this replaces ROW_DELEGATE
   * columns with their delegated columns.
   */
  getColumnsForRow(
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
    row: EvalRowView<TRowMetadata>
  ): Array<StructuredTableColumn> {
    return structuredTable.columns.map((column) =>
      resolveRowDelegateColumn(column, row, structuredTable.delegatedColumns)
    );
  }

  /** For a given schema field, return the existing delegate column, or null if there isn't one. */
  getDelegateForSchemaField(
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
    schemaFieldApiId: string
  ): StructuredTableColumn | null {
    return structuredTable.delegatedColumns.find((col) => col.schemaFieldApiId === schemaFieldApiId) ?? null;
  }

  /** By default, only allow specified tables to be used as destinations for Table Mapping */
  allowAsTableMappingDestination(structuredTable: StructuredTable<TTableMetadata, TRowMetadata>): boolean {
    return DESTINATION_TABLE_TYPES.has(structuredTable.kind);
  }

  enableCustomColumns(structuredTable: StructuredTable<TTableMetadata, TRowMetadata>): boolean {
    return false;
  }

  enableLookupColumns(structuredTable: StructuredTable<TTableMetadata, TRowMetadata>): boolean {
    return false;
  }

  /**
   * Returns the columns that can serve as input sources for lookup columns.
   * Subclasses should call super and append additional columns (e.g. ROW_DELEGATE entity
   * columns in parameter tables) that extractUsableSourceColumns does not recognize,
   * de-duplicating by provenanceUuid.
   */
  getLookupInputColumns(
    structuredTable: StructuredTable<TTableMetadata, TRowMetadata>,
    columnConstraints: Array<ColumnConstraint>
  ): Array<NotebookColumnInfo> {
    return extractUsableSourceColumns(columnConstraints);
  }

  protected getRowAndColumnForCellIndex(
    tableView: EvalTableView<TTableMetadata, TRowMetadata>,
    cellIndex: CellIndex
  ): {row: EvalRowView<TRowMetadata>; column: StructuredTableColumn} {
    const {row: rowIndex, column: columnIndex} = cellIndex;
    const row = tableView.rows[rowIndex];
    const column = tableView.columns[columnIndex];
    return {row, column};
  }
}

export function isStructuredTableMetadata<TTableMetadata>(
  metadata: TableMetadata | undefined
): metadata is StructuredTableToTableMetadata<TTableMetadata> {
  return (
    metadata != null && (metadata as StructuredTableToTableMetadata<TTableMetadata>).schemas !== undefined
  );
}
